import Dropdown from '@components/select/Dropdown';
import cx from 'classnames';
import React, { ReactElement, useCallback, useMemo, useState } from 'react';
import { MdOutlineClear, MdOutlineExpandMore } from 'react-icons/md';

export interface IOption {
  key: string | number;
  label: string | number | ReactElement;
  disabled?: boolean;
  icon?: ReactElement;
}

type ISelectBoxReams = {
  options: IOption[];
  value?: string | string[];
  onChange?: (value: string[] | string | undefined) => void;
  allowClear?: boolean;
  multipleMode?: boolean;
  labelOkButton?: string;
  labelResetButton?: string;
};

const selectBoxBorderClass =
  'tw-border tw-border-solid hover:tw-border-[--color-border-button-outline]';
const selectBoxClass =
  'tw-box-border tw-flex tw-justify-between tw-items-center tw-py-2 tw-px-3 tw-h-[30px] tw-w-full tw-bg-[--surface-background] tw-rounded-lg tw-group tw-overflow-hidden';

const SelectBoxReams: React.FC<ISelectBoxReams> = ({
  multipleMode,
  options,
  value,
  onChange,
  allowClear,
  labelOkButton,
  labelResetButton,
}) => {
  const [focused, setFocused] = useState(false);

  const onDropdownChange = (dropdownOpen: boolean) => {
    setFocused(dropdownOpen);
  };

  const renderLabelSelectBox = useMemo(
    () => options?.find((option) => option?.key === value)?.label,
    [options, value],
  );

  const renderMultipleLabelsSelectBox = useMemo(() => {
    const safeValueArray = Array.isArray(value) ? value : [];
    return (
      <div className="tw-flex tw-flex-wrap tw-gap-1">
        {options
          ?.filter((option) => safeValueArray?.includes(String(option.key)))
          ?.map((option) => (
            <span
              key={option.key}
              className="tw-bg-gray-200 tw-px-2 tw-py-1 tw-rounded tw-text-xs"
            >
              {option.label}
            </span>
          ))}
      </div>
    );
  }, [options, value]);

  const onClearItems = useCallback(
    (e: { stopPropagation: () => void }) => {
      e.stopPropagation();
      if (multipleMode) {
        onChange?.([]);
      } else {
        onChange?.(undefined);
      }
    },
    [multipleMode, onChange],
  );

  return (
    <Dropdown
      multipleMode={multipleMode}
      options={options}
      onChange={onChange}
      value={value}
      onDropdownChange={onDropdownChange}
      enablePopupParentContainer={false}
      labelOkButton={labelOkButton}
      labelResetButton={labelResetButton}
    >
      <div
        className={cx(selectBoxBorderClass, selectBoxClass, {
          'tw-border-[--color-border-button-outline]': focused,
          'tw-border-[--color-border-primary]': !focused,
        })}
      >
        <span className="tw-truncate tw-flex-1">
          {multipleMode ? renderMultipleLabelsSelectBox : renderLabelSelectBox}
        </span>
        <div className="tw-ml-2 tw-relative tw-w-5 tw-h-5">
          {allowClear && value && (
            <MdOutlineClear
              size={20}
              className="tw-absolute tw-top-0 tw-left-0 tw-text-[--color-text-weakest] tw-cursor-pointer tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity"
              onClick={onClearItems}
            />
          )}

          {(!allowClear || !value) && (
            <MdOutlineExpandMore
              size={20}
              className="tw-text-[--color-text-weakest] tw-pointer-events-none"
            />
          )}

          {allowClear && value && (
            <MdOutlineExpandMore
              size={20}
              className="tw-absolute tw-top-0 tw-left-0 tw-text-[--color-text-weakest] tw-opacity-100 group-hover:tw-opacity-0 tw-transition-opacity tw-pointer-events-none"
            />
          )}
        </div>
      </div>
    </Dropdown>
  );
};

export default SelectBoxReams;
